<script setup>
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import apiClient from "@/apiClient";
import CompetitorsView from "@/views/CompetitorsView.vue";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import { apiUrlRoot } from "@/vars";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  BookmarkIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  EyeIcon,
  GlobeAltIcon,
  NewspaperIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";

dayjs.extend(relativeTime);
useBreadcrumbs().setBreadcrumb("News Feed");

const router = useRouter();
const route = useRoute();
const items = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const cursor = ref(null);
const hasMore = ref(false);
const allowedCategories = ["all", "quote", "invoice", "customer", "competitor"];
const category = ref(
  allowedCategories.includes(String(route.query.category))
    ? String(route.query.category)
    : "all",
);
const savedOnly = ref(false);
const unreadOnly = ref(false);
const sentinel = ref(null);
let observer = null;

const categories = [
  { value: "all", label: "For you" },
  { value: "quote", label: "Quotes" },
  { value: "invoice", label: "Invoices" },
  { value: "customer", label: "Customers" },
  { value: "competitor", label: "Competitors" },
];

const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
});

const formatCurrency = (amount, currency = "GBP") => {
  if (amount === null || amount === undefined) return null;
  if (currency === "GBP") return currencyFormatter.format(Number(amount) / 100);
  return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
    Number(amount) / 100,
  );
};
const formatRelative = (date) => (date ? dayjs(date).fromNow() : "just now");
const mediaUrl = (value) => {
  if (!value) return null;
  try {
    return new URL(value, apiUrlRoot).toString();
  } catch {
    return value;
  }
};
const categoryLabel = (value) =>
  categories.find((entry) => entry.value === value)?.label ?? value;
const categoryIcon = (value) =>
  ({
    quote: DocumentTextIcon,
    invoice: BanknotesIcon,
    customer: BuildingOffice2Icon,
    competitor: GlobeAltIcon,
  })[value] ?? NewspaperIcon;
const severityClasses = (severity) =>
  ({
    critical:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200",
    warning:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
    success:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200",
    information:
      "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200",
  })[severity] ?? "border-slate-200 bg-slate-50 text-slate-600";

const loadFeed = async ({ append = false } = {}) => {
  if (loading.value || loadingMore.value || (append && !hasMore.value)) return;
  if (append) loadingMore.value = true;
  else loading.value = true;

  try {
    const response = await apiClient.get("/api/manager/news-feed", {
      params: {
        category: category.value,
        saved: savedOnly.value ? 1 : undefined,
        unread: unreadOnly.value ? 1 : undefined,
        cursor: append ? cursor.value : undefined,
        per_page: 15,
      },
      suppressErrorAlert: true,
    });
    const payload = response.data ?? {};
    items.value = append
      ? [...items.value, ...(payload.data ?? [])]
      : (payload.data ?? []);
    cursor.value = payload.next_cursor ?? null;
    hasMore.value = Boolean(payload.has_more);
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
};

const reload = () => {
  cursor.value = null;
  hasMore.value = false;
  return loadFeed();
};

const selectCategory = (value) => {
  category.value = value;
  const query = { ...route.query };
  if (value === "all") delete query.category;
  else query.category = value;
  if (value !== "competitor") delete query.competitor;
  router.replace({ query });
};

const updateState = async (item, changes) => {
  const response = await apiClient.patch(
    `/api/manager/news-feed/${item.id}/state`,
    changes,
  );
  item.state = response.data;
  if (changes.dismissed || (savedOnly.value && changes.saved === false)) {
    items.value = items.value.filter((entry) => entry.id !== item.id);
  }
};

const openItem = async (item) => {
  if (!item.state.read) await updateState(item, { read: true });
  if (item.action_url) router.push(item.action_url);
};

const watchSentinel = async () => {
  await nextTick();
  observer?.disconnect();
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadFeed({ append: true });
      }
    },
    { rootMargin: "500px" },
  );
  if (sentinel.value) observer.observe(sentinel.value);
};

watch([category, savedOnly, unreadOnly], reload);
watch(
  () => route.query.category,
  (value) => {
    const nextCategory = allowedCategories.includes(String(value))
      ? String(value)
      : "all";
    if (category.value !== nextCategory) category.value = nextCategory;
  },
);
watch(sentinel, watchSentinel);

onMounted(async () => {
  await loadFeed();
  watchSentinel();
});
onUnmounted(() => observer?.disconnect());
</script>

<template>
  <div class="space-y-5 pb-12">
    <div class="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p
          class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
        >
          Management overview
        </p>
        <h1
          class="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100"
        >
          News Feed
        </h1>
        <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
          The latest commercial activity, customer health and market changes.
        </p>
      </div>
    </div>

    <section
      class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      aria-label="Feed filters"
    >
      <div class="flex min-w-0 items-center gap-1 overflow-x-auto">
        <button
          v-for="option in categories"
          :key="option.value"
          type="button"
          class="whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors"
          :class="
            category === option.value
              ? 'border-cyan-600 text-cyan-700 dark:text-cyan-300'
              : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white'
          "
          @click="selectCategory(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
      <div class="flex items-center gap-1 py-2">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
          :class="
            savedOnly
              ? 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-200'
              : 'text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
          "
          @click="savedOnly = !savedOnly"
        >
          <BookmarkIcon class="h-4 w-4" /> Saved
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
          :class="
            unreadOnly
              ? 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-200'
              : 'text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
          "
          @click="unreadOnly = !unreadOnly"
        >
          <EyeIcon class="h-4 w-4" /> Unread
        </button>
      </div>
    </section>

    <CompetitorsView
      v-if="category === 'competitor'"
      embedded
      @refreshed="reload"
    />

    <div v-if="category === 'competitor'" class="flex items-center gap-3 pt-2">
      <div class="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
      <p
        class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400"
      >
        Latest competitor updates
      </p>
      <div class="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    </div>

    <main class="w-full space-y-3">
      <div v-if="loading" class="space-y-3">
        <div
          v-for="index in 3"
          :key="index"
          class="h-48 animate-pulse rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
        />
      </div>

      <template v-else>
        <article
          v-for="item in items"
          :key="item.id"
          class="w-full overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-slate-800"
          :class="
            item.state.read
              ? 'border-slate-200 dark:border-slate-700'
              : 'border-cyan-300 dark:border-cyan-700'
          "
        >
          <div class="p-5 sm:p-6">
            <div class="flex items-start justify-between gap-4">
              <div class="flex min-w-0 items-start gap-3.5">
                <div
                  class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-50 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-300"
                >
                  <img
                    v-if="
                      item.category === 'competitor' &&
                      item.competitor?.logo_url
                    "
                    :src="mediaUrl(item.competitor.logo_url)"
                    :alt="`${item.competitor.name} logo`"
                    class="h-full w-full rounded-full object-contain p-1.5"
                    loading="lazy"
                    referrerpolicy="no-referrer"
                    @error="item.competitor.logo_url = null"
                  />
                  <component
                    v-else
                    :is="categoryIcon(item.category)"
                    class="h-5 w-5"
                  />
                </div>
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2 text-xs">
                    <span
                      class="font-semibold text-slate-500 dark:text-slate-300"
                    >
                      {{ categoryLabel(item.category) }}
                    </span>
                    <span class="text-slate-300 dark:text-slate-600">•</span>
                    <span class="text-slate-400">{{
                      formatRelative(item.occurred_at)
                    }}</span>
                    <span
                      v-if="!item.state.read"
                      class="h-2 w-2 rounded-full bg-cyan-500"
                      title="Unread"
                    />
                  </div>
                  <h2
                    class="mt-1 text-lg font-semibold leading-snug text-slate-900 dark:text-slate-100"
                  >
                    {{ item.title }}
                  </h2>
                </div>
              </div>
              <p
                v-if="item.amount_minor !== null"
                class="shrink-0 text-lg font-semibold text-slate-900 dark:text-slate-100"
              >
                {{ formatCurrency(item.amount_minor, item.currency) }}
              </p>
            </div>

            <p
              v-if="item.summary"
              class="mt-4 max-w-5xl text-[15px] leading-6 text-slate-600 dark:text-slate-300"
            >
              {{ item.summary }}
            </p>

            <div class="mt-4 flex flex-wrap items-center gap-2">
              <span
                v-if="item.severity !== 'information'"
                class="rounded-full border px-2.5 py-1 text-xs font-semibold capitalize"
                :class="severityClasses(item.severity)"
              >
                {{ item.severity }}
              </span>
              <span
                v-if="item.customer"
                class="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-200"
              >
                {{ item.customer.name }}
              </span>
              <span
                v-if="item.competitor"
                class="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-200"
              >
                {{ item.competitor.name }}
              </span>
              <span
                v-if="item.is_ai_summary"
                class="inline-flex items-center gap-1 text-xs text-cyan-600 dark:text-cyan-300"
              >
                <SparklesIcon class="h-3.5 w-3.5" /> Neptune summary
              </span>
            </div>
          </div>

          <img
            v-if="item.image_url"
            :src="mediaUrl(item.image_url)"
            :alt="item.title"
            class="max-h-[34rem] w-full border-t border-slate-100 object-cover dark:border-slate-700"
            loading="lazy"
            referrerpolicy="no-referrer"
            @error="item.image_url = null"
          />

          <div
            class="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-4 py-2.5 dark:border-slate-700"
          >
            <div class="flex items-center gap-1">
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                @click="updateState(item, { saved: !item.state.saved })"
              >
                <BookmarkIcon
                  class="h-4 w-4"
                  :class="item.state.saved ? 'fill-cyan-600 text-cyan-600' : ''"
                />
                {{ item.state.saved ? "Saved" : "Save" }}
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                @click="updateState(item, { dismissed: true })"
              >
                <XMarkIcon class="h-4 w-4" /> Dismiss
              </button>
            </div>
            <button
              v-if="item.action_url"
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-cyan-700 hover:bg-cyan-50 dark:text-cyan-300 dark:hover:bg-cyan-950/30"
              @click="openItem(item)"
            >
              Open <ArrowTopRightOnSquareIcon class="h-4 w-4" />
            </button>
          </div>
        </article>
      </template>

      <div
        v-if="!loading && !items.length"
        class="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center dark:border-slate-600 dark:bg-slate-800"
      >
        <NewspaperIcon class="mx-auto h-10 w-10 text-slate-300" />
        <h2 class="mt-3 font-semibold text-slate-800 dark:text-slate-100">
          Nothing in this view yet
        </h2>
        <p class="mt-1 text-sm text-slate-500">
          New business events will appear here automatically.
        </p>
      </div>

      <div ref="sentinel" class="h-4" />
      <div
        v-if="loadingMore"
        class="flex justify-center py-6 text-sm text-slate-500"
      >
        <ArrowPathIcon class="mr-2 h-5 w-5 animate-spin" /> Loading more
      </div>
    </main>
  </div>
</template>
